import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { ClinicalPageShell } from "@/components/clinical";
import { CrudShellHeader, Panel } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { clientRequestHeaders } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import {
  BRAND_CSV_TEMPLATE_HEADER,
  CATEGORY_CSV_TEMPLATE_HEADER,
  PRODUCT_CSV_TEMPLATE_HEADER,
} from "@/lib/product-api-map";
import { parseProductsCsv } from "@/lib/csv-products";
import { Download, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/app/catalogacion/importar")({
  component: CatalogacionImportarPage,
  head: () => ({ meta: [{ title: "Importar — Catalogación" }] }),
});

type ImportKind = "products" | "categories" | "brands";

/** SCRUM-401: filas por request — mantiene cada lote lejos del timeout de la
 * function serverless y del máximo de requests/min (ver SECURITY_LIMITS.importCsv). */
const PRODUCT_IMPORT_BATCH_SIZE = 150;

function downloadTextFile(filename: string, content: string) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function CatalogacionImportarPage() {
  const tid = useWorkspaceTenantId();
  const [csvProducts, setCsvProducts] = useState("");
  const [csvCategories, setCsvCategories] = useState("");
  const [csvBrands, setCsvBrands] = useState("");
  const [importing, setImporting] = useState<ImportKind | null>(null);
  const [productImportProgress, setProductImportProgress] = useState<{ done: number; total: number } | null>(null);

  const downloadCsv = async (path: string, filename: string) => {
    if (!tid) return;
    const res = await fetch(path, { credentials: "include", headers: clientRequestHeaders(tid) });
    if (!res.ok) {
      toast.error("Error al descargar");
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const readFile = (file: File, setter: (v: string) => void) => {
    const reader = new FileReader();
    reader.onload = () => setter(String(reader.result ?? ""));
    reader.readAsText(file);
  };

  const runImport = async (kind: ImportKind, csv: string, clear: () => void) => {
    if (!tid || !csv.trim()) {
      toast.error("Subí o pegá un CSV primero");
      return;
    }
    const paths: Record<ImportKind, string> = {
      products: "/api/products/import",
      categories: "/api/categories/import",
      brands: "/api/brands/import",
    };
    setImporting(kind);
    try {
      const res = await fetch(paths[kind], {
        method: "POST",
        credentials: "include",
        headers: { ...clientRequestHeaders(tid), "Content-Type": "application/json" },
        body: JSON.stringify({ csv }),
      });
      const data = (await res.json()) as {
        created?: number;
        updated?: number;
        errors?: string[];
        error?: string;
      };
      if (!res.ok) {
        toast.error(data.error ?? "Importación fallida");
        return;
      }
      toast.success(`${data.created ?? 0} creados, ${data.updated ?? 0} actualizados`);
      if (data.errors?.length) {
        toast.message(`${data.errors.length} fila(s) con error`, {
          description: data.errors.slice(0, 3).join(" · "),
        });
      }
      clear();
    } catch {
      toast.error("Error de red");
    } finally {
      setImporting(null);
    }
  };

  // SCRUM-401: importación de productos en lotes con progreso real. El endpoint
  // ya no depende de un único request síncrono para todo el archivo — arriba de
  // PRODUCT_IMPORT_BATCH_SIZE filas eso agotaba el tiempo de la function
  // serverless a mitad de camino y el usuario veía "Error de red" con la mitad
  // del catálogo ya actualizado, sin forma de saber qué pasó.
  const runProductImport = async (csv: string, clear: () => void) => {
    if (!tid || !csv.trim()) {
      toast.error("Subí o pegá un CSV primero");
      return;
    }
    const rows = parseProductsCsv(csv);
    if (!rows.length) {
      toast.error("No se encontraron filas válidas en el CSV");
      return;
    }

    setImporting("products");
    setProductImportProgress({ done: 0, total: rows.length });

    let totalCreated = 0;
    let totalUpdated = 0;
    const allErrors: string[] = [];
    let processed = 0;

    try {
      for (let start = 0; start < rows.length; start += PRODUCT_IMPORT_BATCH_SIZE) {
        const batch = rows.slice(start, start + PRODUCT_IMPORT_BATCH_SIZE);
        const res = await fetch("/api/products/import", {
          method: "POST",
          credentials: "include",
          headers: { ...clientRequestHeaders(tid), "Content-Type": "application/json" },
          body: JSON.stringify({ rows: batch, rowOffset: start }),
        });
        const data = (await res.json().catch(() => null)) as {
          created?: number;
          updated?: number;
          errors?: string[];
          error?: string;
        } | null;
        if (!res.ok || !data) {
          // Error real de procesamiento del lote (no de red) — se corta acá,
          // pero se informa cuánto se alcanzó a procesar antes de fallar.
          toast.error(
            `Importación interrumpida en la fila ${start + 1} de ${rows.length}: ${data?.error ?? "el servidor rechazó el lote"}. ` +
              `Hasta acá: ${totalCreated} creados, ${totalUpdated} actualizados.`,
          );
          return;
        }
        totalCreated += data.created ?? 0;
        totalUpdated += data.updated ?? 0;
        if (data.errors?.length) allErrors.push(...data.errors);
        processed += batch.length;
        setProductImportProgress({ done: processed, total: rows.length });
      }

      toast.success(`Importación completa: ${totalCreated} creados, ${totalUpdated} actualizados de ${rows.length} filas`);
      if (allErrors.length) {
        toast.message(`${allErrors.length} fila(s) con error`, {
          description: allErrors.slice(0, 5).join(" · "),
        });
      }
      clear();
    } catch {
      // Esto sí es un error de red real (conexión caída) — diferenciado del
      // caso anterior, que es un rechazo explícito del servidor.
      toast.error(
        `Se cortó la conexión después de procesar ${processed} de ${rows.length} filas ` +
          `(${totalCreated} creados, ${totalUpdated} actualizados). Podés reintentar con el resto del archivo.`,
      );
    } finally {
      setImporting(null);
      setProductImportProgress(null);
    }
  };

  return (
    <div>
      <CrudShellHeader title="Importar / exportar" description="Productos, categorías y marcas" backHref="/app/catalogacion" backLabel="Volver a Catalogación" />
      <ClinicalPageShell offset="none" className="pt-6">

        <p className="mt-4 text-sm text-muted-foreground">
          El identificador web se genera solo a partir del nombre. Para actualizar filas
          existentes, incluí la columna de ID (exportá antes para obtenerla).
        </p>

        <div className="mt-6 space-y-8">
          <ImportSection
            title="Productos"
            header={PRODUCT_CSV_TEMPLATE_HEADER}
            hint="Categoría y marca por nombre (columnas categoria, marca). Se crean si no existen."
            csv={csvProducts}
            onCsvChange={setCsvProducts}
            importing={importing === "products"}
            progress={productImportProgress}
            onImport={() => void runProductImport(csvProducts, () => setCsvProducts(""))}
            onTemplate={() => downloadTextFile("plantilla-productos.csv", `${PRODUCT_CSV_TEMPLATE_HEADER}\n`)}
            onExport={() => void downloadCsv("/api/products/export", "productos.csv")}
            onFile={(f) => readFile(f, setCsvProducts)}
          />

          <ImportSection
            title="Categorías"
            header={CATEGORY_CSV_TEMPLATE_HEADER}
            hint="parent_id opcional. Sin slug: se asigna automáticamente."
            csv={csvCategories}
            onCsvChange={setCsvCategories}
            importing={importing === "categories"}
            onImport={() => void runImport("categories", csvCategories, () => setCsvCategories(""))}
            onTemplate={() => downloadTextFile("plantilla-categorias.csv", `${CATEGORY_CSV_TEMPLATE_HEADER}\n`)}
            onExport={() => void downloadCsv("/api/categories/export", "categorias.csv")}
            onFile={(f) => readFile(f, setCsvCategories)}
          />

          <ImportSection
            title="Marcas"
            header={BRAND_CSV_TEMPLATE_HEADER}
            hint="Solo nombre obligatorio. Export incluye id y slug para ediciones masivas."
            csv={csvBrands}
            onCsvChange={setCsvBrands}
            importing={importing === "brands"}
            onImport={() => void runImport("brands", csvBrands, () => setCsvBrands(""))}
            onTemplate={() => downloadTextFile("plantilla-marcas.csv", `${BRAND_CSV_TEMPLATE_HEADER}\n`)}
            onExport={() => void downloadCsv("/api/brands/export", "marcas.csv")}
            onFile={(f) => readFile(f, setCsvBrands)}
          />
        </div>
      </ClinicalPageShell>
    </div>
  );
}

function ImportSection({
  title,
  header,
  hint,
  csv,
  onCsvChange,
  importing,
  progress,
  onImport,
  onTemplate,
  onExport,
  onFile,
}: {
  title: string;
  header: string;
  hint: string;
  csv: string;
  onCsvChange: (v: string) => void;
  importing: boolean;
  /** SCRUM-401: progreso del import en lotes (solo Productos lo usa por ahora). */
  progress?: { done: number; total: number } | null;
  onImport: () => void;
  onTemplate: () => void;
  onExport: () => void;
  onFile: (f: File) => void;
}) {
  return (
    <Panel title={title}>
      <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" className="gap-2" onClick={onTemplate}>
          <Download className="h-4 w-4" /> Plantilla vacía
        </Button>
        <Button type="button" variant="outline" size="sm" className="gap-2" onClick={onExport}>
          <Download className="h-4 w-4" /> Exportar actual
        </Button>
      </div>
      <p className="text-xs text-muted-foreground font-mono">{header}</p>
      <p className="text-sm text-muted-foreground">{hint}</p>
      <label className="flex cursor-pointer items-center gap-2 text-sm text-primary hover:underline">
        <Upload className="h-4 w-4" /> Subir .csv
        <input
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
          }}
        />
      </label>
      <textarea
        className="min-h-[120px] w-full rounded-lg border border-input bg-background px-3 py-2 text-sm font-mono"
        value={csv}
        onChange={(e) => onCsvChange(e.target.value)}
        placeholder={header}
      />
      <Button type="button" disabled={importing} onClick={onImport}>
        {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : `Importar ${title.toLowerCase()}`}
      </Button>
      {importing && progress ? (
        <div className="space-y-1">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${Math.min(100, Math.round((progress.done / progress.total) * 100))}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Procesando {progress.done} de {progress.total} filas…
          </p>
        </div>
      ) : null}
      </div>
    </Panel>
  );
}
